import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { InputNumberModule } from 'primeng/inputnumber';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { AppButtonComponent } from '@shared/components/app-button/app-button.component';
import { PermissionService } from '@app/core/services/permission.service';
import {
  CashPaymentMaxAmountConfig,
  CashPaymentMaxAmountService,
} from './services/cash-payment-max-amount.service';

const MIN_MAX_AMOUNT = 0.01;

@Component({
  selector: 'app-cash-payment-max-amount',
  standalone: true,
  imports: [
    FormsModule,
    TranslateModule,
    ToastModule,
    InputNumberModule,
    ToggleSwitchModule,
    AppButtonComponent,
  ],
  providers: [MessageService],
  templateUrl: './cash-payment-max-amount.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CashPaymentMaxAmountComponent implements OnInit {
  private readonly api = inject(CashPaymentMaxAmountService);
  private readonly permissionService = inject(PermissionService);
  private readonly messageService = inject(MessageService);
  private readonly translate = inject(TranslateService);
  private readonly permissionCodes = toSignal(
    this.permissionService.userPermissions$,
    { initialValue: this.permissionService.getCurrentPermissions() },
  );

  canView = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction(
      'settings.order.cash_payment_max_amount.view',
    );
  });

  canEdit = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction(
      'settings.order.cash_payment_max_amount.edit',
    );
  });

  loading = signal(false);
  saving = signal(false);
  configId = signal<number | undefined>(undefined);
  maxAmount = signal<number | null>(5_000_000);
  enabled = signal(true);

  ngOnInit(): void {
    if (!this.canView()) {
      return;
    }
    this.load();
  }

  load(): void {
    this.loading.set(true);
    this.api.getConfig().subscribe({
      next: (cfg) => this.applyConfig(cfg),
      error: () => {
        this.loading.set(false);
        this.showToast('error', 'cash_payment_max_amount.messages.load_failed');
      },
    });
  }

  save(): void {
    if (!this.canEdit()) {
      return;
    }
    const maxAmount = this.maxAmount();
    if (!maxAmount || maxAmount < MIN_MAX_AMOUNT) {
      this.showToast('warn', 'cash_payment_max_amount.messages.invalid_amount');
      return;
    }
    this.saving.set(true);
    this.api
      .upsertConfig({
        id: this.configId(),
        maxAmount,
        enabled: this.enabled(),
      })
      .subscribe({
        next: (cfg) => {
          this.applyConfig(cfg);
          this.saving.set(false);
          this.showToast('success', 'cash_payment_max_amount.messages.save_success');
        },
        error: () => {
          this.saving.set(false);
          this.showToast('error', 'cash_payment_max_amount.messages.save_failed');
        },
      });
  }

  private applyConfig(cfg: CashPaymentMaxAmountConfig): void {
    this.configId.set(cfg.id);
    this.maxAmount.set(cfg.maxAmount == null ? null : Number(cfg.maxAmount));
    this.enabled.set(cfg.enabled !== false);
    this.loading.set(false);
  }

  private showToast(severity: 'success' | 'warn' | 'error', key: string): void {
    const summaryKey =
      severity === 'success'
        ? 'common.messages.success'
        : severity === 'warn'
          ? 'common.messages.warning'
          : 'common.messages.error';
    this.messageService.add({
      severity,
      summary: this.translate.instant(summaryKey),
      detail: this.translate.instant(key),
    });
  }
}
